/**
 * Demo-only "Killer-flow review functions" toggle card.
 *
 * Ronnie's specific ask: "what if a buyer only wants regulatory and
 * medical review, not the full brand/agency/regulatory/medical stack?"
 * This card lets a demoer toggle which reviewer functions appear in
 * the killer-flow stream before kicking off a review. The backend
 * filters `KILLER_FLOW_CANNED` against the saved set, so disabled
 * functions also drop out of the materialised ticket comments and
 * project stages.
 *
 * Phase 1 of the 3-tier asset flow plan. MG superusers only — backend
 * gates on `UserService.hasMeaningfulGigsRole` + `isDemoEnterpriseId`.
 */
import { Button, Card, Checkbox, Flex, Heading, Text } from "@radix-ui/themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
  type DemoReviewFunction,
  DEMO_REVIEW_FUNCTION_LABELS,
  getDemoReviewFunctions,
  updateDemoReviewFunctions,
} from "../../../services/demo";
import { errorMessage } from "../../../utils/http";

const QUERY_KEY = ["demo", "review-functions"] as const;

export function DemoReviewFunctionsCard() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getDemoReviewFunctions,
  });

  // Local edit buffer so toggling checkboxes is instant while the PUT
  // is still in flight. Reconciled from server on initial load and on
  // every successful save.
  const [local, setLocal] = useState<DemoReviewFunction[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (data?.enabledReviewFunctions) {
      setLocal(data.enabledReviewFunctions);
    }
  }, [data?.enabledReviewFunctions]);

  const mutation = useMutation({
    mutationFn: updateDemoReviewFunctions,
    onSuccess: (result) => {
      setLocal(result.enabledReviewFunctions);
      queryClient.setQueryData(QUERY_KEY, {
        success: true,
        enterpriseId: result.enterpriseId,
        enabledReviewFunctions: result.enabledReviewFunctions,
        defaults: data?.defaults ?? [],
      });
      setErrorMsg(null);
    },
    onError: async (err) => {
      setErrorMsg(await errorMessage(err));
    },
  });

  const allOptions = data?.defaults ?? [
    "brand",
    "agency",
    "regulatory",
    "medical",
    "copy",
  ];
  const toggle = (fn: DemoReviewFunction) => {
    setErrorMsg(null);
    setLocal((prev) =>
      prev.includes(fn) ? prev.filter((f) => f !== fn) : [...prev, fn],
    );
  };
  const onSave = () => {
    if (local.length === 0) {
      setErrorMsg("Pick at least one reviewer before saving.");
      return;
    }
    mutation.mutate(local);
  };
  const onResetDefaults = () => {
    if (data?.defaults) {
      setLocal(data.defaults);
      mutation.mutate(data.defaults);
    }
  };
  const dirty =
    data?.enabledReviewFunctions &&
    (local.length !== data.enabledReviewFunctions.length ||
      local.some((fn) => !data.enabledReviewFunctions.includes(fn)));

  return (
    <Card data-testid="settings-card-demo-review-functions">
      <Flex direction="column" gap="3">
        <Flex direction="column" gap="1">
          <Heading as="h2" size="3" weight="bold">
            Which AI reviewers to show
          </Heading>
          <Text size="2" color="gray">
            Pick which AI reviewers appear during the demo. Handy when a buyer
            only cares about regulatory and medical review, for example, and you
            want to hide the others. The reviewers you uncheck won&apos;t show
            up in the 90-second review demo or in any of the comments that
            follow. Click &quot;Reset to defaults&quot; to turn all five back
            on.
          </Text>
        </Flex>
        <Flex
          direction="column"
          gap="2"
          data-testid="demo-review-fn-checkboxes"
        >
          {allOptions.map((fn) => (
            <label
              key={fn}
              className="flex cursor-pointer items-center gap-2"
              data-testid={`demo-review-fn-row-${fn}`}
            >
              <Checkbox
                checked={local.includes(fn)}
                onCheckedChange={() => toggle(fn)}
                disabled={isLoading || mutation.isPending}
                data-testid={`demo-review-fn-checkbox-${fn}`}
              />
              <Text size="2">{DEMO_REVIEW_FUNCTION_LABELS[fn]}</Text>
            </label>
          ))}
        </Flex>
        <Flex align="center" gap="3">
          <Button
            data-testid="demo-review-fn-save"
            onClick={onSave}
            disabled={isLoading || mutation.isPending || !dirty}
          >
            {mutation.isPending ? "Saving…" : "Save"}
          </Button>
          <Button
            data-testid="demo-review-fn-reset"
            variant="soft"
            onClick={onResetDefaults}
            disabled={isLoading || mutation.isPending}
          >
            Reset to defaults
          </Button>
          {mutation.isSuccess && !dirty && !errorMsg ? (
            <Text size="2" color="green">
              Saved.
            </Text>
          ) : null}
          {errorMsg ? (
            <Text size="2" color="red">
              {errorMsg}
            </Text>
          ) : null}
        </Flex>
      </Flex>
    </Card>
  );
}
